import type {
	APIBlogCategoriesBrowseQueryParams,
	APIBlogCategoriesBrowseResult,
	APIBlogCategoryCreateBody,
	APIBlogCategoryCreateResult,
	APIBlogCategoryDeleteResult,
	APIBlogCategoryGetByIdParams,
	APIBlogCategoryGetByIdResult,
	APIBlogCategoryUpdateBody,
	APIBlogCategoryUpdateResult,
	APIBrandAssignProductsBody,
	APIBrandAssignProductsResult,
	APIBrandCreateBody,
	APIBrandCreateResult,
	APIBrandDeleteResult,
	APIBrandGetByIdParams,
	APIBrandGetByIdResult,
	APIBrandsBrowseQueryParams,
	APIBrandsBrowseResult,
	APIBrandUpdateBody,
	APIBrandUpdateResult,
	APICartCreateBody,
	APICartCreateResult,
	APICartDeleteResult,
	APICartGetResult,
	APICategoriesBrowseQueryParams,
	APICategoriesBrowseResult,
	APICategoryCreateBody,
	APICategoryCreateResult,
	APICategoryDeleteResult,
	APICategoryGetByIdParams,
	APICategoryGetByIdResult,
	APICategoryUpdateBody,
	APICategoryUpdateResult,
	APICollectionCreateBody,
	APICollectionCreateResult,
	APICollectionDeleteResult,
	APICollectionGetByIdParams,
	APICollectionGetByIdResult,
	APICollectionsBrowseQueryParams,
	APICollectionsBrowseResult,
	APICollectionUpdateBody,
	APICollectionUpdateResult,
	APIContactMessageCreateBody,
	APIContactMessageCreateResult,
	APICustomerAddressCreateBody,
	APICustomerAddressCreateResult,
	APICustomerGetByIdParams,
	APICustomerGetByIdResult,
	APICustomerOrdersBrowseQueryParams,
	APICustomerOrdersBrowseResult,
	APICustomersBrowseQueryParams,
	APICustomersBrowseResult,
	APICustomerUpdateBody,
	APICustomerUpdateResult,
	APIEventAttendeesBrowseResult,
	APIEventCreateBody,
	APIEventCreateResult,
	APIEventGetByIdParams,
	APIEventGetByIdResult,
	APIEventsBrowseQueryParams,
	APIEventsBrowseResult,
	APIEventUpdateBody,
	APIEventUpdateResult,
	APIInstaviewImagesBrowseParams,
	APIInstaviewImagesBrowseQueryParams,
	APIInstaviewImagesBrowseResult,
	APIInventoryAdjustBody,
	APIInventoryAdjustResult,
	APIInventoryBrowseQueryParams,
	APIInventoryBrowseResult,
	APILegalPageCreateBody,
	APILegalPageCreateQueryParams,
	APILegalPageCreateResult,
	APILegalPageGetByPathQueryParams,
	APILegalPageGetByPathResult,
	APILegalPagesBrowseQueryParams,
	APILegalPagesBrowseResult,
	APILegalPageUpdateBody,
	APILegalPageUpdateQueryParams,
	APILegalPageUpdateResult,
	APIMeGetResult,
	APIOrderGetByIdParams,
	APIOrderGetByIdResult,
	APIOrderRefundGetParams,
	APIOrderRefundGetResult,
	APIOrderRefundsBrowseQueryParams,
	APIOrderRefundsBrowseResult,
	APIOrderRefundsParams,
	APIOrdersBrowseQueryParams,
	APIOrdersBrowseResult,
	APIOrderUpdateBody,
	APIOrderUpdateResult,
	APIPostCommentCreateBody,
	APIPostCommentCreateResult,
	APIPostCommentsBrowseQueryParams,
	APIPostCommentsBrowseResult,
	APIPostCreateBody,
	APIPostCreateResult,
	APIPostDeleteResult,
	APIPostGetByIdParams,
	APIPostGetByIdResult,
	APIPostsBrowseQueryParams,
	APIPostsBrowseResult,
	APIPostUpdateBody,
	APIPostUpdateResult,
	APIProductBatchBody,
	APIProductBatchResult,
	APIProductCreateBody,
	APIProductCreateResult,
	APIProductDeleteResult,
	APIProductFiltersResult,
	APIProductGetByIdParams,
	APIProductGetByIdResult,
	APIProductReviewCreateBody,
	APIProductReviewCreateResult,
	APIProductReviewsBrowseQueryParams,
	APIProductReviewsBrowseResult,
	APIProductsBrowseQueryParams,
	APIProductsBrowseResult,
	APIProductUpdateBody,
	APIProductUpdateResult,
	APISearchQueryParams,
	APISearchResult,
	APISocialsGetResult,
	APISubscriberCreateBody,
	APISubscriberCreateResult,
	APISubscriberDeleteResult,
	APITicketAttendeeGetResult,
	APITicketsGetResult,
	APITicketsUpdateBody,
	APITicketsUpdateResult,
	APIVariantCreateBody,
	APIVariantCreateResult,
	APIVariantGetByIdParams,
	APIVariantGetByIdResult,
	APIVariantUpdateBody,
	APIVariantUpdateResult,
} from "../api-types";
import { env } from "../env";
import { getLogger } from "../logger";
import type { CommerceConfig } from "../types";

export type APICollectionImportMembershipsBody = { file: File | Blob };

export type APICollectionImportMembershipsResult = {
	ok: true;
	processed: number;
	inserted: number;
	skipped_existing: number;
	errors: Array<{
		row: number;
		collection_slug?: string;
		product_slug?: string;
		reason: string;
	}>;
};

export class YNSProvider {
	#config: CommerceConfig;
	#logger = getLogger("YNSProvider");

	constructor(config: CommerceConfig = {}) {
		const token = config.token ?? env.YNS_API_KEY;
		const isStaging = env.YNS_API_KEY?.startsWith("sk-s-");

		if (!token) {
			throw new Error(
				"YNS API key is required. Set YNS_API_KEY environment variable or pass token in config.",
			);
		}
		const endpoint = config.endpoint ?? (isStaging ? "https://yns.cx" : "https://yns.store");

		this.#config = { version: "v1", ...config, token, endpoint };

		this.#logger.debug("YNSProvider initialized", {
			endpoint,
			token: config.token ? `from config` : "from env",
		});
	}

	async #restRequest<T>(
		pathname: `/${string}`,
		method: "GET" | "POST" | "PATCH" | "DELETE" = "GET",
		body?: unknown,
	): Promise<T> {
		const response = await this.#fetchJson(pathname, method, body);
		return this.#parseResponse<T>(response, pathname, method);
	}

	/**
	 * Like {@link #restRequest} but resolves to `null` on a 404 response. For lookups where
	 * "not found" is an expected outcome (e.g. ticket access codes answered with a uniform 404).
	 */
	async #restRequestNullable<T>(pathname: `/${string}`): Promise<T | null> {
		const response = await this.#fetchJson(pathname, "GET");
		if (response.status === 404) {
			return null;
		}
		return this.#parseResponse<T>(response, pathname, "GET");
	}

	async #fetchJson(
		pathname: `/${string}`,
		method: "GET" | "POST" | "PATCH" | "DELETE",
		body?: unknown,
	): Promise<Response> {
		const logger = this.#logger.getLogger("#restRequest");

		const endpoint = `${this.#config.endpoint}/api/${this.#config.version}${pathname}`;
		logger.debug(`Making ${method} request to YNS API: ${endpoint}`);
		return fetch(endpoint, {
			method,
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${this.#config.token}`,
			},
			body: body ? JSON.stringify(body) : undefined,
		});
	}

	async #parseResponse<T>(response: Response, pathname: `/${string}`, method: string): Promise<T> {
		const logger = this.#logger.getLogger("#restRequest");
		const endpoint = `${this.#config.endpoint}/api/${this.#config.version}${pathname}`;

		if (!response.ok) {
			// Handle different error types
			const contentType = response.headers.get("content-type");
			let errorMessage = `YNS REST request failed: ${method} ${endpoint} ${response.status} ${response.statusText}`;

			if (contentType?.includes("application/json")) {
				try {
					const errorData = await response.json();
					errorMessage = errorData.error || errorData.message || errorMessage;
				} catch (error) {
					logger.error("Failed to parse YNS error response as JSON", error);
				}
			} else {
				const errorText = await response.text();
				logger.error(
					`YNS API request failed: ${response.status} ${response.statusText}`,
					errorMessage,
					errorText,
				);
			}

			throw new Error(errorMessage);
		}

		// Check if response is JSON before parsing
		const contentType = response.headers.get("content-type");
		if (!contentType?.includes("application/json")) {
			throw new Error(`YNS API returned ${contentType} instead of JSON for ${pathname}`);
		}

		return response.json();
	}

	async #multipartRequest<T>(pathname: `/${string}`, formData: FormData): Promise<T> {
		const logger = this.#logger.getLogger("#multipartRequest");

		const endpoint = `${this.#config.endpoint}/api/${this.#config.version}${pathname}`;
		logger.debug(`Making multipart POST to YNS API: ${endpoint}`);
		const response = await fetch(endpoint, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${this.#config.token}`,
			},
			body: formData,
		});

		if (!response.ok) {
			const contentType = response.headers.get("content-type");
			let errorMessage = `YNS REST request failed: POST ${endpoint} ${response.status} ${response.statusText}`;

			if (contentType?.includes("application/json")) {
				try {
					const errorData = await response.json();
					errorMessage = errorData.error || errorData.message || errorMessage;
				} catch (error) {
					logger.error("Failed to parse YNS error response as JSON", error);
				}
			} else {
				const errorText = await response.text();
				logger.error(
					`YNS API request failed: ${response.status} ${response.statusText}`,
					errorMessage,
					errorText,
				);
			}

			throw new Error(errorMessage);
		}

		const contentType = response.headers.get("content-type");
		if (!contentType?.includes("application/json")) {
			throw new Error(`YNS API returned ${contentType} instead of JSON for ${pathname}`);
		}

		return response.json();
	}

	async meGet(): Promise<APIMeGetResult> {
		const logger = this.#logger.getLogger("meGet");
		logger.debug("Fetching my information");

		const pathname = `/me` as const;
		const result = await this.#restRequest<APIMeGetResult>(pathname);
		logger.debug("Received my information:", result);
		return result;
	}

	async socialsGet(): Promise<APISocialsGetResult> {
		const logger = this.#logger.getLogger("socialsGet");
		logger.debug("Fetching store social links");

		const pathname = `/socials` as const;
		const result = await this.#restRequest<APISocialsGetResult>(pathname);
		logger.debug("Received store social links:", result);
		return result;
	}

	// Brands ---------------------------------------------------------------

	async brandBrowse(params: APIBrandsBrowseQueryParams = {}): Promise<APIBrandsBrowseResult> {
		const queryParams = new URLSearchParams();
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.offset) queryParams.append("offset", params.offset.toString());
		if (params.query) queryParams.append("query", params.query);
		if (params.active !== undefined) queryParams.append("active", params.active.toString());
		if (params.lang) queryParams.append("lang", params.lang);
		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/brands${searchParams}` as const;
		return this.#restRequest<APIBrandsBrowseResult>(pathname);
	}

	async brandGet(params: APIBrandGetByIdParams): Promise<APIBrandGetByIdResult | null> {
		const pathname = `/brands/${params.idOrSlug}` as const;
		return this.#restRequest<APIBrandGetByIdResult>(pathname);
	}

	async brandCreate(body: APIBrandCreateBody): Promise<APIBrandCreateResult> {
		return this.#restRequest<APIBrandCreateResult>("/brands", "POST", body);
	}

	async brandUpdate(params: APIBrandGetByIdParams, body: APIBrandUpdateBody): Promise<APIBrandUpdateResult> {
		const pathname = `/brands/${params.idOrSlug}` as const;
		return this.#restRequest<APIBrandUpdateResult>(pathname, "PATCH", body);
	}

	async brandDelete(params: APIBrandGetByIdParams): Promise<APIBrandDeleteResult> {
		const pathname = `/brands/${params.idOrSlug}` as const;
		return this.#restRequest<APIBrandDeleteResult>(pathname, "DELETE");
	}

	async brandAssignProducts(
		params: APIBrandGetByIdParams,
		body: APIBrandAssignProductsBody,
	): Promise<APIBrandAssignProductsResult> {
		const pathname = `/brands/${params.idOrSlug}/products` as const;
		return this.#restRequest<APIBrandAssignProductsResult>(pathname, "POST", body);
	}

	// Order refunds --------------------------------------------------------

	async orderRefundsBrowse(
		params: APIOrderRefundsParams & APIOrderRefundsBrowseQueryParams,
	): Promise<APIOrderRefundsBrowseResult> {
		const queryParams = new URLSearchParams();
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.offset) queryParams.append("offset", params.offset.toString());
		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/orders/${params.id}/refunds${searchParams}` as const;
		return this.#restRequest<APIOrderRefundsBrowseResult>(pathname);
	}

	async orderRefundGet(params: APIOrderRefundGetParams): Promise<APIOrderRefundGetResult | null> {
		const pathname = `/orders/${params.id}/refunds/${params.refundId}` as const;
		return this.#restRequest<APIOrderRefundGetResult>(pathname);
	}

	// Product bulk ---------------------------------------------------------

	async productBatch(body: APIProductBatchBody): Promise<APIProductBatchResult> {
		return this.#restRequest<APIProductBatchResult>("/products/batch", "POST", body);
	}

	// Cart -----------------------------------------------------------------

	async cartDelete(params: { cartId: string }): Promise<APICartDeleteResult> {
		const pathname = `/carts/${params.cartId}` as const;
		return this.#restRequest<APICartDeleteResult>(pathname, "DELETE");
	}

	async productBrowse(params: APIProductsBrowseQueryParams): Promise<APIProductsBrowseResult> {
		const logger = this.#logger.getLogger("productBrowse");
		logger.debug("Browsing products with params:", params);

		const queryParams = new URLSearchParams();
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.offset) queryParams.append("offset", params.offset.toString());
		if (params.category) queryParams.append("category", params.category);
		if (params.collection) queryParams.append("collection", params.collection);
		if (params.brand) queryParams.append("brand", params.brand);
		if (params.priceMin !== undefined) queryParams.append("priceMin", params.priceMin.toString());
		if (params.priceMax !== undefined) queryParams.append("priceMax", params.priceMax.toString());
		if (params.vts) queryParams.append("vts", params.vts);
		if (params.query) queryParams.append("query", params.query);
		if (params.active !== undefined) queryParams.append("active", params.active.toString());
		if (params.orderBy) queryParams.append("orderBy", params.orderBy);
		if (params.orderDirection) queryParams.append("orderDirection", params.orderDirection);

		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/products${searchParams}` as const;
		logger.debug("Constructed pathname:", pathname);
		const result = await this.#restRequest<APIProductsBrowseResult>(pathname);
		logger.debug("Received product browse result:", { meta: result.meta });
		return result;
	}

	/**
	 * Available storefront filter facets for the store: price bounds, variant
	 * types/values, and active categories, collections, and brands. Pair with
	 * `productBrowse`'s `collection`, `brand`, `priceMin`/`priceMax`, and `vts`
	 * params to build a filter UI.
	 */
	async productFilters(): Promise<APIProductFiltersResult> {
		const result = await this.#restRequest<APIProductFiltersResult>("/products/filters");
		return result;
	}

	async productGet(params: APIProductGetByIdParams): Promise<APIProductGetByIdResult | null> {
		const pathname = `/products/${params.idOrSlug}` as const;

		const result = await this.#restRequest<APIProductGetByIdResult>(pathname);

		if (!result) {
			return null;
		}

		return result;
	}

	async orderBrowse(params: APIOrdersBrowseQueryParams): Promise<APIOrdersBrowseResult> {
		const logger = this.#logger.getLogger("orderBrowse");
		logger.debug("Browsing orders with params:", params);

		const queryParams = new URLSearchParams();

		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.offset) queryParams.append("offset", params.offset.toString());

		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/orders${searchParams}` as const;
		logger.debug("Constructed pathname:", pathname);
		const result = await this.#restRequest<APIOrdersBrowseResult>(pathname);
		logger.debug("Received orders browse result:", { meta: result.meta });
		return result;
	}

	async orderGet(params: APIOrderGetByIdParams): Promise<APIOrderGetByIdResult | null> {
		const pathname = `/orders/${params.id}` as const;

		const result = await this.#restRequest<APIOrderGetByIdResult>(pathname);

		if (!result) {
			return null;
		}

		return result;
	}

	// @todo
	// async cartAdd(params: APICartAddBody): Promise<APICartAddResult> {
	// 	const body = {
	// 		variantId: params.variantId,
	// 		cartId: params.cartId,
	// 		quantity: params.quantity,
	// 		subscriptionId: params.subscriptionId,
	// 	};

	// 	const result = await this.#restRequest<APICartCreateResult>("/carts", "PATCH", body);
	// 	return result;
	// }

	async cartUpsert(body: APICartCreateBody): Promise<APICartCreateResult> {
		const result = await this.#restRequest<APICartCreateResult>("/carts", "POST", body);
		return result;
	}

	/**
	 * Add a configured bundle to the cart in one call. `selections` are the customer's per-group
	 * choices; always-included (forced) items may be omitted — the server fills them in and
	 * validates the rest against the bundle's groups. Omit `cartId` to start a new cart. The
	 * returned cart prices the bundle line (componentTotal × (1 − discount)).
	 */
	async cartAddBundle(params: {
		bundleId: string;
		selections: NonNullable<APICartCreateBody["selections"]>;
		cartId?: string;
		currency?: string;
	}): Promise<APICartCreateResult> {
		const body: APICartCreateBody = {
			bundleId: params.bundleId,
			selections: params.selections,
			...(params.cartId ? { cartId: params.cartId } : {}),
			...(params.currency ? { currency: params.currency } : {}),
		};
		const result = await this.#restRequest<APICartCreateResult>("/carts", "POST", body);
		return result;
	}

	async cartRemoveItem(params: { cartId: string; variantId: string }): Promise<APICartGetResult> {
		const pathname = `/carts/${params.cartId}/line-items/${params.variantId}` as const;
		return this.#restRequest<APICartGetResult>(pathname, "DELETE");
	}

	async cartGet(params: { cartId: string }): Promise<APICartGetResult | null> {
		const pathname = `/carts/${params.cartId}` as const;
		const result = await this.#restRequest<APICartGetResult>(pathname);
		return result;
	}

	async collectionGet(params: APICollectionGetByIdParams): Promise<APICollectionGetByIdResult | null> {
		const pathname = `/collections/${params.idOrSlug}` as const;

		const result = await this.#restRequest<APICollectionGetByIdResult>(pathname);
		return result;
	}

	async collectionBrowse(params: APICollectionsBrowseQueryParams): Promise<APICollectionsBrowseResult> {
		const queryParams = new URLSearchParams();
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.offset) queryParams.append("offset", params.offset.toString());
		if (params.query) queryParams.append("query", params.query);
		if (params.active !== undefined) queryParams.append("active", params.active.toString());
		if (params.group) queryParams.append("group", params.group);

		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/collections${searchParams}` as const;

		const result = await this.#restRequest<APICollectionsBrowseResult>(pathname);
		return result;
	}

	async collectionCreate(body: APICollectionCreateBody): Promise<APICollectionCreateResult> {
		return this.#restRequest<APICollectionCreateResult>("/collections", "POST", body);
	}

	async collectionUpdate(
		params: APICollectionGetByIdParams,
		body: APICollectionUpdateBody,
	): Promise<APICollectionUpdateResult> {
		const pathname = `/collections/${params.idOrSlug}` as const;
		return this.#restRequest<APICollectionUpdateResult>(pathname, "PATCH", body);
	}

	async collectionDelete(params: APICollectionGetByIdParams): Promise<APICollectionDeleteResult> {
		const pathname = `/collections/${params.idOrSlug}` as const;
		return this.#restRequest<APICollectionDeleteResult>(pathname, "DELETE");
	}

	async collectionImportMemberships(
		body: APICollectionImportMembershipsBody,
	): Promise<APICollectionImportMembershipsResult> {
		const formData = new FormData();
		formData.append("file", body.file);
		return this.#multipartRequest<APICollectionImportMembershipsResult>(
			"/collections/import-memberships",
			formData,
		);
	}

	async categoryGet(params: APICategoryGetByIdParams): Promise<APICategoryGetByIdResult | null> {
		const pathname = `/categories/${params.idOrSlug}` as const;

		const result = await this.#restRequest<APICategoryGetByIdResult>(pathname);
		return result;
	}

	async categoriesBrowse(params: APICategoriesBrowseQueryParams): Promise<APICategoriesBrowseResult> {
		const queryParams = new URLSearchParams();
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.offset) queryParams.append("offset", params.offset.toString());
		if (params.query) queryParams.append("query", params.query);
		if (params.active !== undefined) queryParams.append("active", params.active.toString());

		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/categories${searchParams}` as const;

		const result = await this.#restRequest<APICategoriesBrowseResult>(pathname);
		return result;
	}

	// ============================================
	// Posts
	// ============================================

	async postBrowse(params: APIPostsBrowseQueryParams = {}): Promise<APIPostsBrowseResult> {
		const queryParams = new URLSearchParams();
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.offset) queryParams.append("offset", params.offset.toString());
		if (params.query) queryParams.append("query", params.query);
		if (params.active !== undefined) queryParams.append("active", params.active.toString());
		if (params.tag) queryParams.append("tag", params.tag);
		if (params.categoryId) queryParams.append("categoryId", params.categoryId);

		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/posts${searchParams}` as const;

		return this.#restRequest<APIPostsBrowseResult>(pathname);
	}

	async postGet(params: APIPostGetByIdParams): Promise<APIPostGetByIdResult | null> {
		const pathname = `/posts/${params.idOrSlug}` as const;
		return this.#restRequest<APIPostGetByIdResult>(pathname);
	}

	async postCreate(body: APIPostCreateBody): Promise<APIPostCreateResult> {
		return this.#restRequest<APIPostCreateResult>("/posts", "POST", body);
	}

	async postUpdate(params: APIPostGetByIdParams, body: APIPostUpdateBody): Promise<APIPostUpdateResult> {
		const pathname = `/posts/${params.idOrSlug}` as const;
		return this.#restRequest<APIPostUpdateResult>(pathname, "PATCH", body);
	}

	async postDelete(params: APIPostGetByIdParams): Promise<APIPostDeleteResult> {
		const pathname = `/posts/${params.idOrSlug}` as const;
		return this.#restRequest<APIPostDeleteResult>(pathname, "DELETE");
	}

	async postCommentsBrowse(
		params: APIPostGetByIdParams,
		queryParams: APIPostCommentsBrowseQueryParams = {},
	): Promise<APIPostCommentsBrowseResult> {
		const urlParams = new URLSearchParams();
		if (queryParams.limit) urlParams.append("limit", queryParams.limit.toString());
		if (queryParams.offset) urlParams.append("offset", queryParams.offset.toString());

		const searchParams = urlParams.size ? `?${urlParams}` : "";
		const pathname = `/posts/${params.idOrSlug}/comments${searchParams}` as const;

		return this.#restRequest<APIPostCommentsBrowseResult>(pathname);
	}

	async postCommentCreate(
		params: APIPostGetByIdParams,
		body: APIPostCommentCreateBody,
	): Promise<APIPostCommentCreateResult> {
		const pathname = `/posts/${params.idOrSlug}/comments` as const;
		return this.#restRequest<APIPostCommentCreateResult>(pathname, "POST", body);
	}

	// ============================================
	// Blog Categories
	// ============================================

	async blogCategoryBrowse(
		params: APIBlogCategoriesBrowseQueryParams = {},
	): Promise<APIBlogCategoriesBrowseResult> {
		const queryParams = new URLSearchParams();
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.offset) queryParams.append("offset", params.offset.toString());
		if (params.query) queryParams.append("query", params.query);
		if (params.active !== undefined) queryParams.append("active", params.active.toString());

		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/blog-categories${searchParams}` as const;

		return this.#restRequest<APIBlogCategoriesBrowseResult>(pathname);
	}

	async blogCategoryGet(params: APIBlogCategoryGetByIdParams): Promise<APIBlogCategoryGetByIdResult | null> {
		const pathname = `/blog-categories/${params.idOrSlug}` as const;
		return this.#restRequest<APIBlogCategoryGetByIdResult>(pathname);
	}

	async blogCategoryCreate(body: APIBlogCategoryCreateBody): Promise<APIBlogCategoryCreateResult> {
		return this.#restRequest<APIBlogCategoryCreateResult>("/blog-categories", "POST", body);
	}

	async blogCategoryUpdate(
		params: APIBlogCategoryGetByIdParams,
		body: APIBlogCategoryUpdateBody,
	): Promise<APIBlogCategoryUpdateResult> {
		const pathname = `/blog-categories/${params.idOrSlug}` as const;
		return this.#restRequest<APIBlogCategoryUpdateResult>(pathname, "PATCH", body);
	}

	async blogCategoryDelete(params: APIBlogCategoryGetByIdParams): Promise<APIBlogCategoryDeleteResult> {
		const pathname = `/blog-categories/${params.idOrSlug}` as const;
		return this.#restRequest<APIBlogCategoryDeleteResult>(pathname, "DELETE");
	}

	// ============================================
	// Customers
	// ============================================

	async customerBrowse(params: APICustomersBrowseQueryParams = {}): Promise<APICustomersBrowseResult> {
		const queryParams = new URLSearchParams();
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.offset) queryParams.append("offset", params.offset.toString());
		if (params.search) queryParams.append("search", params.search);

		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/customers${searchParams}` as const;

		return this.#restRequest<APICustomersBrowseResult>(pathname);
	}

	async customerGet(params: APICustomerGetByIdParams): Promise<APICustomerGetByIdResult | null> {
		const pathname = `/customers/${params.id}` as const;
		return this.#restRequest<APICustomerGetByIdResult>(pathname);
	}

	async customerUpdate(
		params: APICustomerGetByIdParams,
		body: APICustomerUpdateBody,
	): Promise<APICustomerUpdateResult> {
		const pathname = `/customers/${params.id}` as const;
		return this.#restRequest<APICustomerUpdateResult>(pathname, "PATCH", body);
	}

	async customerAddressCreate(
		params: APICustomerGetByIdParams,
		body: APICustomerAddressCreateBody,
	): Promise<APICustomerAddressCreateResult> {
		const pathname = `/customers/${params.id}/addresses` as const;
		return this.#restRequest<APICustomerAddressCreateResult>(pathname, "POST", body);
	}

	async customerAddressDelete(params: { customerId: string; addressId: string }): Promise<void> {
		const pathname = `/customers/${params.customerId}/addresses/${params.addressId}` as const;
		await this.#restRequest<unknown>(pathname, "DELETE");
	}

	async customerOrdersBrowse(
		params: APICustomerGetByIdParams,
		queryParams: APICustomerOrdersBrowseQueryParams = {},
	): Promise<APICustomerOrdersBrowseResult> {
		const urlParams = new URLSearchParams();
		if (queryParams.limit) urlParams.append("limit", queryParams.limit.toString());
		if (queryParams.offset) urlParams.append("offset", queryParams.offset.toString());

		const searchParams = urlParams.size ? `?${urlParams}` : "";
		const pathname = `/customers/${params.id}/orders${searchParams}` as const;

		return this.#restRequest<APICustomerOrdersBrowseResult>(pathname);
	}

	// ============================================
	// Inventory
	// ============================================

	async inventoryBrowse(params: APIInventoryBrowseQueryParams = {}): Promise<APIInventoryBrowseResult> {
		const queryParams = new URLSearchParams();
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.cursor) queryParams.append("cursor", params.cursor);
		if (params.lowStock !== undefined) queryParams.append("lowStock", params.lowStock.toString());

		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/inventory${searchParams}` as const;

		return this.#restRequest<APIInventoryBrowseResult>(pathname);
	}

	async inventoryAdjust(sku: string, body: APIInventoryAdjustBody): Promise<APIInventoryAdjustResult> {
		const pathname = `/inventory/${encodeURIComponent(sku)}/adjust` as const;
		return this.#restRequest<APIInventoryAdjustResult>(pathname, "POST", body);
	}

	// ============================================
	// Variants
	// ============================================

	async variantGet(params: APIVariantGetByIdParams): Promise<APIVariantGetByIdResult | null> {
		const pathname = `/variants/${params.idOrSku}` as const;
		return this.#restRequest<APIVariantGetByIdResult>(pathname);
	}

	async variantUpdate(
		params: APIVariantGetByIdParams,
		body: APIVariantUpdateBody,
	): Promise<APIVariantUpdateResult> {
		const pathname = `/variants/${params.idOrSku}` as const;
		return this.#restRequest<APIVariantUpdateResult>(pathname, "PATCH", body);
	}

	async variantDelete(params: APIVariantGetByIdParams): Promise<void> {
		const pathname = `/variants/${params.idOrSku}` as const;
		await this.#restRequest<unknown>(pathname, "DELETE");
	}

	async variantCreate(productId: string, body: APIVariantCreateBody): Promise<APIVariantCreateResult> {
		const pathname = `/products/${productId}/variants` as const;
		return this.#restRequest<APIVariantCreateResult>(pathname, "POST", body);
	}

	// ============================================
	// Subscribers
	// ============================================

	async subscriberCreate(body: APISubscriberCreateBody): Promise<APISubscriberCreateResult> {
		return this.#restRequest<APISubscriberCreateResult>("/subscribers", "POST", body);
	}

	async subscriberDelete(email: string): Promise<APISubscriberDeleteResult> {
		const pathname = `/subscribers?email=${encodeURIComponent(email)}` as const;
		return this.#restRequest<APISubscriberDeleteResult>(pathname, "DELETE");
	}

	// ============================================
	// Contact Messages
	// ============================================

	async contactMessageCreate(body: APIContactMessageCreateBody): Promise<APIContactMessageCreateResult> {
		return this.#restRequest<APIContactMessageCreateResult>("/contact-messages", "POST", body);
	}

	// ============================================
	// Product mutations
	// ============================================

	async productCreate(body: APIProductCreateBody): Promise<APIProductCreateResult> {
		return this.#restRequest<APIProductCreateResult>("/products", "POST", body);
	}

	async productUpdate(
		params: APIProductGetByIdParams,
		body: APIProductUpdateBody,
	): Promise<APIProductUpdateResult> {
		const pathname = `/products/${params.idOrSlug}` as const;
		return this.#restRequest<APIProductUpdateResult>(pathname, "PATCH", body);
	}

	async productDelete(params: APIProductGetByIdParams): Promise<APIProductDeleteResult> {
		const pathname = `/products/${params.idOrSlug}` as const;
		return this.#restRequest<APIProductDeleteResult>(pathname, "DELETE");
	}

	// ============================================
	// Product Reviews
	// ============================================

	async productReviewsBrowse(
		params: APIProductGetByIdParams,
		queryParams: APIProductReviewsBrowseQueryParams = {},
	): Promise<APIProductReviewsBrowseResult> {
		const urlParams = new URLSearchParams();
		if (queryParams.limit) urlParams.append("limit", queryParams.limit.toString());
		if (queryParams.offset) urlParams.append("offset", queryParams.offset.toString());

		const searchParams = urlParams.size ? `?${urlParams}` : "";
		const pathname = `/products/${params.idOrSlug}/reviews${searchParams}` as const;

		return this.#restRequest<APIProductReviewsBrowseResult>(pathname);
	}

	async productReviewCreate(
		params: APIProductGetByIdParams,
		body: APIProductReviewCreateBody,
	): Promise<APIProductReviewCreateResult> {
		const pathname = `/products/${params.idOrSlug}/reviews` as const;
		return this.#restRequest<APIProductReviewCreateResult>(pathname, "POST", body);
	}

	// ============================================
	// Events (products flagged `flags.event` with a single non-shippable ticket variant)
	// ============================================

	async eventBrowse(params: APIEventsBrowseQueryParams = {}): Promise<APIEventsBrowseResult> {
		const logger = this.#logger.getLogger("eventBrowse");
		const queryParams = new URLSearchParams();
		if (params.offset !== undefined) queryParams.append("offset", params.offset.toString());
		if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());

		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/events${searchParams}` as const;
		logger.debug("Browsing events:", pathname);
		return this.#restRequest<APIEventsBrowseResult>(pathname);
	}

	async eventGet(params: APIEventGetByIdParams): Promise<APIEventGetByIdResult> {
		const pathname = `/events/${params.idOrSlug}` as const;
		return this.#restRequest<APIEventGetByIdResult>(pathname);
	}

	async eventCreate(body: APIEventCreateBody): Promise<APIEventCreateResult> {
		return this.#restRequest<APIEventCreateResult>("/events", "POST", body);
	}

	async eventUpdate(params: APIEventGetByIdParams, body: APIEventUpdateBody): Promise<APIEventUpdateResult> {
		const pathname = `/events/${params.idOrSlug}` as const;
		return this.#restRequest<APIEventUpdateResult>(pathname, "PATCH", body);
	}

	async eventAttendeesBrowse(params: APIEventGetByIdParams): Promise<APIEventAttendeesBrowseResult> {
		const pathname = `/events/${params.idOrSlug}/attendees` as const;
		return this.#restRequest<APIEventAttendeesBrowseResult>(pathname);
	}

	// ============================================
	// Tickets (no-login buyer/attendee access to purchased event tickets)
	// ============================================

	/**
	 * Buyer ticket lookup: the order's 6-character access code plus the purchase email.
	 * Resolves to `null` on mismatch — the API answers a uniform 404 for an unknown code,
	 * an email that doesn't match, or an order without tickets.
	 */
	async ticketsGet(params: { code: string; email: string }): Promise<APITicketsGetResult | null> {
		const logger = this.#logger.getLogger("ticketsGet");
		const queryParams = new URLSearchParams({ email: params.email });
		const pathname = `/tickets/${encodeURIComponent(params.code)}?${queryParams}` as const;
		logger.debug("Fetching ticket bundle:", pathname);
		return this.#restRequestNullable<APITicketsGetResult>(pathname);
	}

	/**
	 * Buyer bulk-edit of attendee names/emails. The body carries the purchase email; seat
	 * tokens not belonging to the order are silently ignored. Throws on code/email mismatch
	 * (404) or when the order is cancelled/refunded (409).
	 */
	async ticketsUpdate(params: { code: string }, body: APITicketsUpdateBody): Promise<APITicketsUpdateResult> {
		const pathname = `/tickets/${encodeURIComponent(params.code)}` as const;
		return this.#restRequest<APITicketsUpdateResult>(pathname, "PATCH", body);
	}

	/**
	 * View-only attendee ticket by per-seat token (the capability embedded in the attendee
	 * link/QR). Resolves to `null` for an unknown token. Attendee details can only be edited
	 * by the buyer via {@link ticketsUpdate} — attendee links must not escalate.
	 */
	async ticketAttendeeGet(params: { token: string }): Promise<APITicketAttendeeGetResult | null> {
		const pathname = `/tickets/attendee/${encodeURIComponent(params.token)}` as const;
		return this.#restRequestNullable<APITicketAttendeeGetResult>(pathname);
	}

	// ============================================
	// Category mutations
	// ============================================

	async categoryCreate(body: APICategoryCreateBody): Promise<APICategoryCreateResult> {
		return this.#restRequest<APICategoryCreateResult>("/categories", "POST", body);
	}

	async categoryUpdate(
		params: APICategoryGetByIdParams,
		body: APICategoryUpdateBody,
	): Promise<APICategoryUpdateResult> {
		const pathname = `/categories/${params.idOrSlug}` as const;
		return this.#restRequest<APICategoryUpdateResult>(pathname, "PATCH", body);
	}

	async categoryDelete(params: APICategoryGetByIdParams): Promise<APICategoryDeleteResult> {
		const pathname = `/categories/${params.idOrSlug}` as const;
		return this.#restRequest<APICategoryDeleteResult>(pathname, "DELETE");
	}

	// ============================================
	// Order mutations
	// ============================================

	async orderUpdate(params: APIOrderGetByIdParams, body: APIOrderUpdateBody): Promise<APIOrderUpdateResult> {
		const pathname = `/orders/${params.id}` as const;
		return this.#restRequest<APIOrderUpdateResult>(pathname, "PATCH", body);
	}

	// ============================================
	// Legal Pages
	// ============================================

	async legalPageBrowse(
		queryParams: APILegalPagesBrowseQueryParams = {},
	): Promise<APILegalPagesBrowseResult> {
		return this.request<APILegalPagesBrowseResult>("/legal-pages", {
			query: queryParams,
		});
	}

	async legalPageGet(
		key: string,
		queryParams: APILegalPageGetByPathQueryParams = {},
	): Promise<APILegalPageGetByPathResult> {
		const normalized = key.startsWith("/") ? key.slice(1) : key;
		const pathname = `/legal-pages/${encodeURIComponent(normalized)}` as const;
		return this.request<APILegalPageGetByPathResult>(pathname, {
			query: queryParams,
		});
	}

	async legalPageCreate(
		body: APILegalPageCreateBody,
		queryParams: APILegalPageCreateQueryParams = {},
	): Promise<APILegalPageCreateResult> {
		return this.request<APILegalPageCreateResult, APILegalPageCreateBody>("/legal-pages", {
			method: "POST",
			body,
			query: queryParams,
		});
	}

	async legalPageUpdate(
		key: string,
		body: APILegalPageUpdateBody,
		queryParams: APILegalPageUpdateQueryParams = {},
	): Promise<APILegalPageUpdateResult> {
		const normalized = key.startsWith("/") ? key.slice(1) : key;
		const pathname = `/legal-pages/${encodeURIComponent(normalized)}` as const;
		return this.request<APILegalPageUpdateResult, APILegalPageUpdateBody>(pathname, {
			method: "PATCH",
			body,
			query: queryParams,
		});
	}

	async search(params: APISearchQueryParams): Promise<APISearchResult> {
		return this.request<APISearchResult>("/search", {
			query: params,
		});
	}

	// ============================================
	// Instaview
	// ============================================

	async instaviewImagesBrowse(
		params: APIInstaviewImagesBrowseParams,
		queryParams: APIInstaviewImagesBrowseQueryParams = {},
	): Promise<APIInstaviewImagesBrowseResult> {
		const urlParams = new URLSearchParams();
		if (queryParams.limit) urlParams.append("limit", queryParams.limit.toString());
		if (queryParams.cursor) urlParams.append("cursor", queryParams.cursor);

		const searchParams = urlParams.size ? `?${urlParams}` : "";
		const pathname = `/instaview/accounts/${params.handle}/images${searchParams}` as const;

		return this.#restRequest<APIInstaviewImagesBrowseResult>(pathname);
	}

	/**
	 * Make a raw API request to any endpoint.
	 * Use this for new API features not yet supported by typed methods.
	 *
	 * @example
	 * // GET request (default method)
	 * const webhooks = await provider.request<Webhook[]>('/webhooks');
	 *
	 * // POST with typed body
	 * const webhook = await provider.request<Webhook, CreateWebhookBody>('/webhooks', {
	 *   method: 'POST',
	 *   body: { url: 'https://...' }
	 * });
	 *
	 * // GET with query parameters
	 * const products = await provider.request<Product[]>('/products', {
	 *   query: { limit: 10, category: 'shoes' }
	 * });
	 *
	 * // Path parameters via template literals
	 * const product = await provider.request<Product>(`/products/${id}`);
	 */
	async request<TResponse = unknown, TBody = unknown>(
		pathname: `/${string}`,
		options?: {
			method?: "GET" | "POST" | "PATCH" | "DELETE";
			body?: TBody;
			query?: Record<string, string | number | boolean | null | undefined>;
		},
	) {
		const queryEntries = options?.query
			? Object.entries(options.query).filter((entry): entry is [string, string | number | boolean] => {
					const [, value] = entry;
					return value !== null && value !== undefined;
				})
			: [];
		const queryString =
			queryEntries.length > 0 ? `?${new URLSearchParams(queryEntries.map(([k, v]) => [k, String(v)]))}` : "";

		return this.#restRequest<TResponse>(`${pathname}${queryString}`, options?.method ?? "GET", options?.body);
	}
}
